import classNames from 'classnames';
import React, { ChangeEvent, FC, memo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import isFunction from 'lodash/isFunction';

import { ValidationListErrors } from 'src/components/common/Errors/list';
import { UiButton } from 'src/components/UI/Button';
import { UiLoader } from 'src/components/UI/loaders';
import { UiModalCommonButtons } from 'src/components/UI/modal/_buttons';
import { UiElementLightbox } from 'src/components/UI/UiElementLightbox';
import { CSS_ANIMATION_DURATION } from 'src/config';
import { ChildrenPropType } from 'src/utils/types';

export const MODAL_ANIMATION_DURATION = CSS_ANIMATION_DURATION / 2;

type ButtonProps = Omit<UiModalCommonButtons, 'close'>;

export type UiModalProps = {
  header?: ChildrenPropType;
  children?: ChildrenPropType;
  id?: string;
  modalClassName?: string;
  bodyClassName?: string;
  loading?: boolean;
  validationErrors?: string[];
  preview?: string;
  onCloseValidationErrors?: () => void;
  customFooter?: (onHideClick: () => void, btnProps: ButtonProps) => React.ReactElement | null;
  customClose?: (onClose: () => void) => React.ReactElement | null;
  noFooter?: boolean;
  showCloseModalButtonWhenNoHeader?: boolean;
  backdropClassName?: string;
  enforceFocus?: boolean;
  success?: ChildrenPropType;
  noBackdropClickClose?: boolean;
} & ButtonProps &
  ({ disableClose?: false; onHide: () => void } | { disableClose: true; onHide?: () => void });

export const UiModal: FC<UiModalProps> = memo(
  ({
    onHide,
    modalClassName,
    bodyClassName,
    id,
    loading,
    children,
    header,
    validationErrors,
    onCloseValidationErrors,
    customFooter,
    customClose,
    preview,
    noFooter = false,
    showCloseModalButtonWhenNoHeader = false,
    disableClose,
    backdropClassName,
    enforceFocus = false,
    success,
    noBackdropClickClose,
    ...btnsProps
  }) => {
    const [localShow, setLocalShow] = useState(true);

    const { t } = useTranslation();

    const closeModal = () => {
      if (disableClose) {
        return;
      }

      setLocalShow(false);

      /** TIMING HACK */
      if (isFunction(onHide)) {
        setTimeout(() => {
          onHide();
        }, MODAL_ANIMATION_DURATION);
      }
    };

    const errors = Array.isArray(validationErrors) && (
      <ValidationListErrors data={validationErrors} onClose={onCloseValidationErrors} />
    );

    return (
      <Modal
        enforceFocus={enforceFocus}
        id={id}
        show={localShow}
        onHide={closeModal}
        className={classNames('r-modal modal-v2', modalClassName, {
          'help-text-overlay': preview,
          loading,
        })}
        backdropClassName={classNames('r-modal-backdrop', backdropClassName)}
        centered
        size="lg"
        onClick={({ target: { classList } }: ChangeEvent<HTMLDivElement>) => {
          /** outer click hack */
          if (!noBackdropClickClose && classList.contains('modal-dialog')) {
            closeModal();
          }
        }}
        backdrop={noBackdropClickClose ? 'static' : undefined}
      >
        {header && (
          <Modal.Header>
            {!disableClose &&
              /** custom button because of styles */
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              (customClose ? customClose(closeModal) : <button type="button" className="close" onClick={closeModal} />)}
            <Modal.Title>{header}</Modal.Title>
            {errors}
            {success && <div className="alert alert-success">{success}</div>}
          </Modal.Header>
        )}

        {!disableClose &&
          showCloseModalButtonWhenNoHeader &&
          (customClose ? (
            customClose(closeModal)
          ) : (
            <UiButton tag="a" className="close" data-dismiss="modal" aria-hidden="true" onClick={closeModal}>
              <i className="material-icons">clear</i>
            </UiButton>
          ))}

        <Modal.Body className={bodyClassName}>
          {!header && errors}
          {loading ? <UiLoader centered /> : children}
          {preview && (
            <div className="help-text-overlay-item">
              <div className="help-text-overlay-item-preview">
                <UiElementLightbox
                  src={preview}
                  render={({ onShow }) => (
                    <UiButton tag="a" className="help-text-overlay-item-preview-link" onClick={onShow}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <g clipPath="url(#clip0_141_1520)">
                          <path
                            /* eslint-disable-next-line max-len */
                            d="M-0.175 5.41675C-0.175 5.7434 0.0900169 6.00841 0.416667 6.00841C0.743316 6.00841 1.00833 5.7434 1.00833 5.41675V3.75008C1.00833 3.15756 1.49082 2.67508 2.08333 2.67508H3.75C4.07665 2.67508 4.34167 2.41006 4.34167 2.08341C4.34167 1.75676 4.07665 1.49175 3.75 1.49175H2.08333C0.837471 1.49175 -0.175 2.50514 -0.175 3.75008V5.41675Z"
                            fill="#0084FF"
                            stroke="#0084FF"
                            strokeWidth="0.35"
                          />
                          <path
                            /* eslint-disable-next-line max-len */
                            d="M18.9917 5.41675C18.9917 5.7434 19.2567 6.00841 19.5834 6.00841C19.91 6.00841 20.175 5.7434 20.175 5.41675V3.75008C20.175 2.50514 19.1626 1.49175 17.9167 1.49175H16.25C15.9234 1.49175 15.6584 1.75676 15.6584 2.08341C15.6584 2.41006 15.9234 2.67508 16.25 2.67508H17.9167C18.5092 2.67508 18.9917 3.15756 18.9917 3.75008V5.41675Z"
                            fill="#0084FF"
                            stroke="#0084FF"
                            strokeWidth="0.35"
                          />
                          <path
                            /* eslint-disable-next-line max-len */
                            d="M2.08333 18.5082H3.75C4.07665 18.5082 4.34167 18.2432 4.34167 17.9165C4.34167 17.5899 4.07665 17.3248 3.75 17.3248H2.08333C1.49082 17.3248 1.00833 16.8424 1.00833 16.2498V14.5832C1.00833 14.2565 0.743317 13.9915 0.416667 13.9915C0.0900167 13.9915 -0.175 14.2565 -0.175 14.5832V16.2498C-0.175 17.4948 0.837471 18.5082 2.08333 18.5082Z"
                            fill="#0084FF"
                            stroke="#0084FF"
                            strokeWidth="0.35"
                          />
                          <path
                            /* eslint-disable-next-line max-len */
                            d="M16.25 18.5082H17.9167C19.1626 18.5082 20.175 17.4948 20.175 16.2498V14.5832C20.175 14.2565 19.91 13.9915 19.5834 13.9915C19.2567 13.9915 18.9917 14.2565 18.9917 14.5832V16.2498C18.9917 16.8424 18.5092 17.3248 17.9167 17.3248H16.25C15.9234 17.3248 15.6584 17.5899 15.6584 17.9165C15.6584 18.2432 15.9234 18.5082 16.25 18.5082Z"
                            fill="#0084FF"
                            stroke="#0084FF"
                            strokeWidth="0.35"
                          />
                          <path
                            /* eslint-disable-next-line max-len */
                            d="M3.17613 9.85826C3.15246 9.95119 3.15246 10.0487 3.17611 10.1417C3.90522 13.1139 6.71809 15.175 10 15.175C13.282 15.175 16.0948 13.1139 16.824 10.1417C16.8476 10.0488 16.8476 9.95127 16.824 9.85835C16.0949 6.8861 13.282 4.825 10 4.825C6.71812 4.825 3.90527 6.88605 3.17613 9.85826ZM10 13.9917C7.29905 13.9917 5.01045 12.3484 4.36137 10C5.01044 7.65157 7.29823 6.00833 10 6.00833C12.7018 6.00833 14.9896 7.65157 15.6387 10C14.9896 12.3484 12.701 13.9917 10 13.9917Z"
                            fill="#0084FF"
                            stroke="#0084FF"
                            strokeWidth="0.35"
                          />
                          <path
                            /* eslint-disable-next-line max-len */
                            d="M7.325 10C7.325 11.475 8.52502 12.675 10 12.675C11.475 12.675 12.675 11.475 12.675 10C12.675 8.52502 11.475 7.325 10 7.325C8.52502 7.325 7.325 8.52502 7.325 10ZM8.50833 10C8.50833 9.17748 9.17748 8.50833 10 8.50833C10.8225 8.50833 11.4917 9.17748 11.4917 10C11.4917 10.8225 10.8225 11.4917 10 11.4917C9.17748 11.4917 8.50833 10.8225 8.50833 10Z"
                            fill="#0084FF"
                            stroke="#0084FF"
                            strokeWidth="0.35"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_141_1520">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      &nbsp;{t('common.preview')}
                    </UiButton>
                  )}
                />
              </div>
            </div>
          )}
        </Modal.Body>

        {!noFooter && (
          <Modal.Footer>
            {customFooter ? (
              customFooter(closeModal, btnsProps)
            ) : (
              <UiModalCommonButtons
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...btnsProps}
                close={closeModal}
                successDisabled={loading || btnsProps.successDisabled}
              />
            )}
          </Modal.Footer>
        )}
      </Modal>
    );
  }
);
